import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import apiOffers from 'api/offers';
import ContentSizedTextarea from 'components/layout/forms/ContentSizedTextarea';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';
import { ADDITIONAL_OFFER } from 'services/enums/offers/offerTypes';
import { addMessage } from 'store/messages/actions';

import 'style/offers/OfferBasicInfo.css';

/**
 * Internal memo of the whole offer. It is not printed to the offer PDF and it can be
 * edited also after the offer has been sent, so it is saved separately from the offer.
 */
const OfferMemo = ({
  initialMemo,
  offerId,
  readOnly,
  type,
}: {
  initialMemo: string;
  offerId: number;
  // The user can only read the offer
  readOnly?: boolean;
  type: string;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.common]);

  const [memo, setMemo] = useState(initialMemo);
  const [savedMemo, setSavedMemo] = useState(initialMemo);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);

    try {
      if (type === ADDITIONAL_OFFER) {
        await apiOffers.additionalOffers.patchMemo(offerId, memo);
      } else {
        await apiOffers.patchMemo(offerId, memo);
      }

      setSavedMemo(memo);
      dispatch(addMessage(t(`${translations.offers}:Memo saved`)));
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:Memo failed`)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="offer-memo">
      <Row>
        <Col>
          <ContentSizedTextarea
            disabled={readOnly}
            id="offer-memo"
            label={t(`${translations.offers}:Offer memo`)}
            name="memo"
            onChange={(event) => setMemo(event.target.value)}
            value={memo}
          />
          <p className="offer-memo-info">
            {t(`${translations.offers}:Offer memo info`)}
          </p>
        </Col>
      </Row>
      {/* On its own line, so the action buttons of the offer below do not wrap next to it */}
      {readOnly ? null : (
        <div className="offer-memo-actions">
          <PrimaryButton
            className="button-one-line"
            disabled={saving || memo === savedMemo}
            onClick={save}
          >
            {t(`${translations.offers}:Save memo`).toLocaleUpperCase()}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

export default OfferMemo;
